import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AI_OUT_OF_CREDIT, isOutOfCredit } from '../../utils/apiError'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowPathIcon, CalculatorIcon, CheckCircleIcon, ExclamationTriangleIcon, SparklesIcon } from '@heroicons/react/24/outline'
import recipesAPI from '../../services/recipes'
import { NUTRIENTS, computePreview, weakMatches } from '../../utils/recipeNutrition'

const EMPTY = { kcal: '', protein_g: '', carbs_g: '', fat_g: '', fiber_g: '', sugars_g: '' }

const round = (value, digits = 1) =>
  value === null || value === undefined ? null : Math.round(Number(value) * 10 ** digits) / 10 ** digits

const isWeak = (suggestion) => ['low', 'unsicher'].includes(suggestion?.confidence)

/** One ingredient: the matched food, an AI estimate the user confirms, or values typed by hand. */
function IngredientRow({ row, choice, onChange, onEstimate, estimating, failed, disabled,
                        weight, onWeightChange }) {
  const { t } = useTranslation()
  // QA 2026-09-16: a low-confidence match (e.g. "Spelt sourdough" → a sourdough roll) must not be
  // pre-selected — one click would store the wrong food into plans and the diary.
  const weakMatch = isWeak(row.suggestion)
  const mode = choice?.mode ?? (row.per_100g ? 'stored' : row.suggestion && !weakMatch ? 'matched' : 'none')
  const values = choice?.per_100g ?? row.per_100g ?? row.suggestion?.per_100g ?? null

  return (
    <li className="py-3 border-b border-gray-100 last:border-b-0" data-testid={`nutrition-ingredient-${row.ingredient_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-gray-900 font-medium">{row.name}</span>
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {row.amount} {row.unit}
          {row.grams ? ` · ${round(row.grams, 0)} g` : ''}
        </span>
      </div>

      {/* VH-265: a count has no weight this code can derive — what one piece weighs
          depends on what it is. Rather than invent a number (which is how a calorie
          count starts lying) or count it as zero (which is what used to happen, and
          cost recipe 40 its eggs), the row asks. */}
      {(!row.grams || row.weight_given) && !row.zero_by_nature && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-amber-700">
              {t('recipes.nutrition.weightNeeded', { amount: row.amount, unit: row.unit })}
            </span>
            <input
              type="number"
              min="0"
              step="any"
              value={weight ?? ''}
              disabled={disabled}
              onChange={(e) => onWeightChange(e.target.value === '' ? '' : Number(e.target.value))}
              className="form-input w-24"
              data-testid={`nutrition-weight-${row.ingredient_id}`}
            />
            <span className="text-gray-500">g</span>
          </label>
          {row.remembered_grams && Number(weight) === Number(row.remembered_grams) && (
            <span className="text-xs text-gray-500" data-testid={`nutrition-weight-remembered-${row.ingredient_id}`}>
              {t('recipes.nutrition.weightRemembered')}
            </span>
          )}
        </div>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
        {/* Already linked to a food: say which one, so a saved match is not anonymous. */}
        {!row.suggestion && row.food_name && (
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name={`source-${row.ingredient_id}`}
              checked={mode === 'stored' || mode === 'matched'}
              disabled={disabled}
              onChange={() => onChange({ mode: 'matched', food_id: row.food_id, per_100g: row.per_100g })}
            />
            <span>{t('recipes.nutrition.matched', { name: row.food_name })}</span>
          </label>
        )}
        {row.suggestion && (
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name={`source-${row.ingredient_id}`}
              checked={mode === 'matched'}
              disabled={disabled}
              onChange={() => onChange({ mode: 'matched', food_id: row.suggestion.food_id, per_100g: row.suggestion.per_100g })}
            />
            <span>
              {t('recipes.nutrition.matched', { name: row.suggestion.food_name })}
              <span className={`text-xs ml-1 ${weakMatch ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                ({t(`recipes.nutrition.confidence.${row.suggestion.confidence}`, row.suggestion.confidence)})
              </span>
              {weakMatch && (
                <span className="block text-xs text-amber-700">{t('recipes.nutrition.checkWeakMatch')}</span>
              )}
            </span>
          </label>
        )}
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name={`source-${row.ingredient_id}`}
            checked={mode === 'estimate'}
            disabled={disabled || !choice?.estimate}
            onChange={() => onChange({ mode: 'estimate', per_100g: choice.estimate })}
          />
          <span>{t('recipes.nutrition.estimate')}</span>
        </label>
        <button
          type="button"
          className="text-xs underline text-teal-700"
          disabled={disabled || estimating}
          onClick={() => onEstimate(row.ingredient_id)}
        >
          {estimating ? t('recipes.nutrition.estimating') : t('recipes.nutrition.askAI')}
        </button>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name={`source-${row.ingredient_id}`}
            checked={mode === 'manual'}
            disabled={disabled}
            onChange={() => onChange({ mode: 'manual', per_100g: { ...EMPTY, ...(values ?? {}) } })}
          />
          <span>{t('recipes.nutrition.manual')}</span>
        </label>
      </div>

      {mode === 'manual' ? (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {NUTRIENTS.map((key) => (
            <label key={key} className="text-xs text-gray-600">
              {t(`recipes.nutrition.nutrient.${key}`)}
              <input
                type="number"
                min="0"
                step="0.1"
                className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm"
                aria-label={`${row.name} ${t(`recipes.nutrition.nutrient.${key}`)}`}
                value={choice?.per_100g?.[key] ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ mode: 'manual', per_100g: { ...choice.per_100g, [key]: e.target.value } })
                }
              />
            </label>
          ))}
        </div>
      ) : values ? (
        <p className="mt-1 text-xs text-gray-600" data-testid={`nutrition-values-${row.ingredient_id}`}>
          {t('recipes.nutrition.per100g', {
            kcal: round(values.kcal),
            protein: round(values.protein_g),
            carbs: round(values.carbs_g),
            fat: round(values.fat_g),
          })}
        </p>
      ) : failed ? (
        <p className="mt-1 text-xs text-amber-700" data-testid={`nutrition-estimate-failed-${row.ingredient_id}`}>
          {t('recipes.nutrition.estimateUnavailable')}
        </p>
      ) : row.zero_by_nature ? (
        // Water and salt have no calories on purpose: saying "no values yet" here would be a
        // false alarm that pushes the user to invent numbers.
        <p className="mt-1 text-xs text-gray-500">{t('recipes.nutrition.zeroByNature')}</p>
      ) : (
        <p className="mt-1 text-xs text-amber-700">{t('recipes.nutrition.unresolved')}</p>
      )}
    </li>
  )
}

/**
 * VH-236: "Calculate nutrition" for a recipe version.
 *
 * Every ingredient gets its values from a matched food, an AI estimate the user confirms, or values
 * typed by hand; the finished weight makes per-100 g refer to the baked item rather than the dough.
 * The server does all the arithmetic — this panel only collects the decisions.
 *
 * VH-255: one action, one message. Opening the panel calculates the whole recipe (cancellable);
 * the older "Estimate all missing ingredients" button did a subset of the same thing and its
 * result contradicted the one next to it. What is still unvalued is said once, in the line under
 * the button — not again as a warning block. A running preview and a pre-save check exist because
 * an earlier version happily saved 81 kcal a slice for a loaf of bread: one ingredient had values,
 * the other six did not, and nothing said so.
 */
export function RecipeNutritionPanel({ recipeId, versionId, servings }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [choices, setChoices] = useState({})
  // VH-265: weights the user supplies for amounts that have no gram conversion.
  const [weights, setWeights] = useState({})
  const [finishedWeight, setFinishedWeight] = useState('')
  const [servingCount, setServingCount] = useState(servings ?? 1)
  const [estimatingId, setEstimatingId] = useState(null)
  const [failedIds, setFailedIds] = useState([])
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [confirmSave, setConfirmSave] = useState(null)
  // What the last whole-recipe calculation said: which rows changed, and how the finished weight
  // was arrived at. Shown rather than applied invisibly.
  const [calcResult, setCalcResult] = useState(null)
  // Initialise the form once per version: a refetch must not throw away what the user has typed.
  const initialisedFor = useRef(null)
  const autoStartedFor = useRef(null)
  const abortRef = useRef(null)

  const nutrition = useQuery({
    queryKey: ['recipeNutrition', recipeId, versionId],
    queryFn: () => recipesAPI.getNutrition(recipeId, versionId).then((r) => r.data),
    enabled: open,
  })

  useEffect(() => {
    const data = nutrition.data
    if (!data || initialisedFor.current === data.version_id) return
    initialisedFor.current = data.version_id
    setFinishedWeight(
      data.summary.finished_weight_g ?? (data.summary.raw_weight_g ? round(data.summary.raw_weight_g, 0) : ''),
    )
    setServingCount(data.summary.servings)
    // VH-269: a weight said once is offered again, pre-filled and still editable — never
    // applied without being shown.
    setWeights(
      Object.fromEntries(
        data.ingredients
          .filter((row) => (row.weight_given && row.grams) || (!row.grams && row.remembered_grams))
          // VH-271: a weight already saved on the row is shown in the same field, to correct it.
          .map((row) => [row.ingredient_id, row.weight_given ? row.grams : row.remembered_grams]),
      ),
    )
    setChoices(
      Object.fromEntries(
        data.ingredients.map((row) => [
          row.ingredient_id,
          row.per_100g
            ? { mode: 'stored', source: row.source, food_id: row.food_id, per_100g: row.per_100g }
            : row.suggestion && !isWeak(row.suggestion)
              ? { mode: 'matched', food_id: row.suggestion.food_id, per_100g: row.suggestion.per_100g }
              : { mode: 'none' },
        ]),
      ),
    )
  }, [nutrition.data])

  const estimate = useMutation({
    mutationFn: (ingredientId) =>
      recipesAPI.estimateIngredientNutrition(recipeId, ingredientId, versionId).then((r) => r.data),
    onSuccess: (data) => {
      setFailedIds((previous) => previous.filter((id) => id !== data.ingredient_id))
      setChoices((previous) => ({
        ...previous,
        [data.ingredient_id]: { mode: 'estimate', estimate: data.per_100g, per_100g: data.per_100g },
      }))
    },
    onError: () => setError(t('recipes.nutrition.errors.estimate')),
    onSettled: () => setEstimatingId(null),
  })

  // VH-237: the whole recipe in one press. The rows come back with old -> new, so the panel can
  // say what it changed instead of swapping a value the user is looking at.
  const applyCalculation = useCallback((results) => {
    setChoices((previous) => {
      const next = { ...previous }
      results.forEach((result) => {
        if (!result.ok || !result.per_100g) return
        // The server already kept what the user typed; `kept` says so. Everything else is
        // replaced on purpose — a wrong food match is what this button exists to repair.
        if (result.kept) return
        next[result.ingredient_id] = {
          mode: 'estimate', estimate: result.per_100g, per_100g: result.per_100g,
        }
      })
      return next
    })
    setFailedIds(results.filter((result) => !result.ok).map((result) => result.ingredient_id))
  }, [])

  const calculate = useMutation({
    mutationFn: () => {
      abortRef.current = new AbortController()
      return recipesAPI.calculateNutrition(recipeId, versionId, abortRef.current.signal).then((r) => r.data)
    },
    onSuccess: (data) => {
      setError(null)
      applyCalculation(data.ingredients ?? [])
      const proposal = data.finished_weight ?? {}
      // The finished weight is the other half of the complaint: it had to be typed, and a wrong
      // guess (750 g for a pot of 750 g dry lentils) silently distorted every per-100 g figure.
      if (proposal.finished_weight_g && (!finishedWeight || proposal.stored_is_impossible)) {
        setFinishedWeight(String(proposal.finished_weight_g))
      }
      setCalcResult(data)
      setNotice(
        data.failed > 0
          ? t('recipes.nutrition.calculatedPartial', { done: data.calculated, failed: data.failed })
          : t('recipes.nutrition.calculatedDone', { count: data.calculated, changed: data.changed }),
      )
    },
    onError: (err) => {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        setNotice(t('recipes.nutrition.estimateCancelled'))
        return
      }
      // VH-238: name the billing state rather than "could not estimate" — every ingredient would
      // fail for the same reason and no retry can succeed.
      setError(isOutOfCredit(err) || err?.response?.data?.detail === AI_OUT_OF_CREDIT
        ? t('nutrition.errors.aiOutOfCredit')
        : t('recipes.nutrition.errors.estimateAll'))
    },
    onSettled: () => { abortRef.current = null },
  })

  const save = useMutation({
    mutationFn: (payload) => recipesAPI.saveNutrition(recipeId, payload, versionId).then((r) => r.data),
    onSuccess: () => {
      setError(null)
      setConfirmSave(null)
      queryClient.invalidateQueries({ queryKey: ['recipeNutrition', recipeId] })
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] })
      queryClient.invalidateQueries({ queryKey: ['recipes'] })
    },
    onError: () => setError(t('recipes.nutrition.errors.save')),
  })

  const rows = useMemo(() => nutrition.data?.ingredients ?? [], [nutrition.data])
  const busy = save.isPending || calculate.isPending
  const preview = useMemo(
    () => computePreview({ rows, choices, finishedWeightG: finishedWeight, servings: servingCount }),
    [rows, choices, finishedWeight, servingCount],
  )
  const weak = useMemo(() => weakMatches(rows, choices), [rows, choices])
  // Rows still without values after a calculation — named once, under the button.
  const missing = useMemo(
    () =>
      rows.filter((row) => {
        const choice = choices[row.ingredient_id]
        // Water and salt are zero on purpose, so they are never "missing".
        return !row.zero_by_nature && !row.per_100g && (!choice || choice.mode === 'none')
      }),
    [rows, choices],
  )

  // VH-255: opening the panel on a recipe with nothing valued starts the one calculation by
  // itself — that is what the title promises. It does NOT start when values already exist: a
  // whole-recipe run is minutes of AI, and re-running it must stay the user's decision (the
  // button is right there).
  useEffect(() => {
    const data = nutrition.data
    if (!open || !data || autoStartedFor.current === data.version_id || calculate.isPending) return
    const anyValues = data.ingredients.some(
      (row) => row.per_100g || (row.suggestion && !isWeak(row.suggestion)),
    )
    const anyMissing = data.ingredients.some(
      (row) => !row.per_100g && !row.zero_by_nature && row.grams > 0,
    )
    if (anyValues || !anyMissing) return
    autoStartedFor.current = data.version_id
    calculate.mutate()
  }, [open, nutrition.data, calculate])

  const buildPayload = (confirmIncomplete) => {
    const ingredients = rows
      .map((row) => {
        const choice = choices[row.ingredient_id]
        if (!choice || choice.mode === 'none') return null
        // VH-265: a weight the user supplied for an amount the code cannot convert.
        const grams = row.grams && !row.weight_given ? undefined : Number(weights[row.ingredient_id]) || undefined
        if (choice.food_id && (choice.mode === 'matched' || choice.source === 'matched')) {
          return { ingredient_id: row.ingredient_id, source: 'matched', food_id: choice.food_id, grams }
        }
        const values = Object.fromEntries(NUTRIENTS.map((key) => [key, Number(choice.per_100g?.[key] ?? 0)]))
        // A row loaded from the database keeps the source it was saved with.
        const source = choice.mode === 'stored' ? (choice.source ?? 'manual') : choice.mode === 'estimate' ? 'estimate' : 'manual'
        return { ingredient_id: row.ingredient_id, source, per_100g: values, grams }
      })
      .filter(Boolean)
    return {
      finished_weight_g: finishedWeight ? Number(finishedWeight) : null,
      servings: Number(servingCount) || 1,
      ingredients,
      confirm_incomplete: Boolean(confirmIncomplete),
    }
  }

  const submit = () => {
    // Everything that makes a saved result untrustworthy, named before it is stored.
    const blockers = []
    if (preview.missing.length > 0) {
      blockers.push({
        code: 'missing_values',
        text: t('recipes.nutrition.guard.missingValues', {
          names: preview.missing.map((row) => row.name).join(', '),
        }),
      })
    }
    if (preview.warnings.some((warning) => warning.code === 'finished_weight_equals_raw')) {
      blockers.push({
        code: 'finished_weight_equals_raw',
        text: t('recipes.nutrition.guard.finishedWeightEqualsRaw', { raw: round(preview.rawWeightG, 0) }),
      })
    }
    if (weak.length > 0) {
      blockers.push({
        code: 'weak_match',
        text: t('recipes.nutrition.guard.weakMatch', { names: weak.map((row) => row.name).join(', ') }),
      })
    }
    const density = preview.warnings.find((warning) => warning.code === 'energy_density_implausible')
    if (density) {
      blockers.push({
        code: 'energy_density_implausible',
        text: t('recipes.nutrition.warning.energy_density_implausible', {
          density: round(density.kcal_per_100g_solids),
          floor: density.expected_at_least,
        }),
      })
    }
    if (blockers.length > 0) {
      setConfirmSave(blockers)
      return
    }
    save.mutate(buildPayload(false))
  }

  // Once the user has chosen values, the stored summary is out of date by definition — showing it
  // next to the live preview would be two different answers to the same question.
  const summary = save.data?.summary ?? (preview.hasValues ? null : nutrition.data?.summary)
  // After a save the stored result says the same thing as the preview, so only show the preview
  // again once the user changes something.
  const previewDiffers =
    !save.data?.summary?.per_100g ||
    Math.abs((save.data.summary.per_100g.kcal ?? 0) - (preview.per100g?.kcal ?? 0)) > 0.1

  return (
    <div className="bg-surface-container rounded-lg border border-gray-200 p-5" data-testid="recipe-nutrition-panel">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <CalculatorIcon className="h-5 w-5 text-teal-700" aria-hidden="true" />
          {t('recipes.nutrition.title')}
        </h2>
        <button type="button" className="btn-outline text-sm" onClick={() => setOpen((v) => !v)}>
          {open ? t('recipes.nutrition.hide') : t('recipes.nutrition.calculate')}
        </button>
      </div>
      <p className="text-sm text-gray-600 mt-1">{t('recipes.nutrition.intro')}</p>

      {open && (
        <div className="mt-4 space-y-4">
          {nutrition.isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
          {nutrition.isError && <p className="text-sm text-red-600" role="alert">{t('recipes.nutrition.errors.load')}</p>}

          {nutrition.data && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn-primary text-sm flex items-center gap-1"
                  data-testid="calculate-nutrition"
                  disabled={busy || rows.length === 0}
                  onClick={() => {
                    setNotice(null)
                    setCalcResult(null)
                    calculate.mutate()
                  }}
                >
                  <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                  {t('recipes.nutrition.calculateFromIngredients')}
                </button>
                {calculate.isPending && (
                  <span className="text-sm text-gray-700 flex items-center gap-2" role="status"
                    data-testid="nutrition-calculating">
                    <ArrowPathIcon className="h-4 w-4 animate-spin text-teal-700" aria-hidden="true" />
                    {t('recipes.nutrition.calculating', { count: rows.length })}
                    <button
                      type="button"
                      className="text-xs underline text-teal-700"
                      onClick={() => abortRef.current?.abort()}
                    >
                      {t('common.cancel')}
                    </button>
                  </span>
                )}
                {!calculate.isPending && notice && (
                  <span className="text-sm text-gray-700 flex items-center gap-1" data-testid="nutrition-notice"
                    role="status">
                    {calculate.isSuccess && (
                      <CheckCircleIcon className="h-4 w-4 text-teal-700 flex-shrink-0" aria-hidden="true" />
                    )}
                    {notice}
                  </span>
                )}
                {!calculate.isPending && missing.length > 0 && (
                  <span className="text-sm text-amber-800" data-testid="nutrition-missing">
                    {t('recipes.nutrition.warning.ingredients_without_nutrition', {
                      names: missing.map((row) => row.name).join(', '),
                    })}
                  </span>
                )}
              </div>

              {/* A value that changes under the user's eyes has to say so: the reported fault was a
                  wrong value presented as settled, and replacing it silently would repeat that. */}
              {calcResult && calcResult.changed > 0 && (
                <ul className="text-xs text-gray-600 space-y-0.5" data-testid="nutrition-changes">
                  {calcResult.ingredients
                    .filter((row) => row.changed)
                    .map((row) => (
                      <li key={row.ingredient_id}>
                        {t('recipes.nutrition.changedRow', {
                          name: row.name,
                          before: round(row.previous_per_100g?.kcal, 0) ?? '—',
                          after: round(row.per_100g?.kcal, 0) ?? '—',
                        })}
                      </li>
                    ))}
                </ul>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">
                  {t('recipes.nutrition.finishedWeight')}
                  <input
                    type="number"
                    min="1"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                    aria-label={t('recipes.nutrition.finishedWeight')}
                    value={finishedWeight}
                    onChange={(e) => setFinishedWeight(e.target.value)}
                  />
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {t('recipes.nutrition.finishedWeightHint', {
                      raw: round(nutrition.data.summary.raw_weight_g, 0) ?? '—',
                    })}
                  </span>
                  {/* VH-237: where the proposed weight comes from, in the user's own ingredients —
                      raw plus what the dry pulses drink, minus what the pot loses. */}
                  {calcResult?.finished_weight?.finished_weight_g && (
                    <span className="block text-xs text-teal-700 mt-0.5" data-testid="finished-weight-proposal">
                      {t('recipes.nutrition.finishedWeightProposed', {
                        weight: round(calcResult.finished_weight.finished_weight_g, 0),
                        raw: round(calcResult.finished_weight.raw_weight_g, 0),
                        absorbed: round(calcResult.finished_weight.water_absorbed_g, 0),
                        evaporated: round(calcResult.finished_weight.evaporated_g, 0),
                      })}
                      {' '}
                      {/* VH-255: one tap to take it — Reto: "if required I can adopt the total weight". */}
                      {String(round(calcResult.finished_weight.finished_weight_g, 0)) !== String(finishedWeight) && (
                        <button
                          type="button"
                          className="underline font-medium"
                          data-testid="adopt-finished-weight"
                          onClick={() =>
                            setFinishedWeight(String(round(calcResult.finished_weight.finished_weight_g, 0)))}
                        >
                          {t('recipes.nutrition.adoptFinishedWeight')}
                        </button>
                      )}
                    </span>
                  )}
                  {calcResult?.finished_weight?.stored_is_impossible && (
                    <span className="block text-xs text-amber-700 mt-0.5" role="alert" data-testid="finished-weight-impossible">
                      {t('recipes.nutrition.finishedWeightImpossible', {
                        weight: round(calcResult.finished_weight.stored_g, 0),
                      })}
                    </span>
                  )}
                </label>
                <label className="text-sm text-gray-700">
                  {t('recipes.nutrition.servings')}
                  <input
                    type="number"
                    min="1"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                    aria-label={t('recipes.nutrition.servings')}
                    value={servingCount}
                    onChange={(e) => setServingCount(e.target.value)}
                  />
                </label>
              </div>

              <ul className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <IngredientRow
                    key={row.ingredient_id}
                    row={row}
                    choice={choices[row.ingredient_id]}
                    estimating={estimatingId === row.ingredient_id}
                    failed={failedIds.includes(row.ingredient_id)}
                    disabled={save.isPending}
                    onChange={(choice) =>
                      setChoices((previous) => ({ ...previous, [row.ingredient_id]: { ...previous[row.ingredient_id], ...choice } }))
                    }
                    weight={weights[row.ingredient_id]}
                    onWeightChange={(value) =>
                      setWeights((previous) => ({ ...previous, [row.ingredient_id]: value }))
                    }
                    onEstimate={(id) => {
                      setEstimatingId(id)
                      estimate.mutate(id)
                    }}
                  />
                ))}
              </ul>

              {/* Live preview: what Save would store, and whether it is even plausible. */}
              {preview.hasValues && previewDiffers && (
                <div className="rounded-md border border-teal-200 bg-teal-50 p-3 text-sm" data-testid="recipe-nutrition-preview">
                  <p className="font-medium text-gray-900">
                    {t('recipes.nutrition.previewTitle')}{' '}
                    {t('recipes.nutrition.per100gResult', {
                      kcal: round(preview.per100g?.kcal),
                      protein: round(preview.per100g?.protein_g),
                      carbs: round(preview.per100g?.carbs_g),
                      fat: round(preview.per100g?.fat_g),
                    })}
                  </p>
                  <p className="text-gray-700 mt-0.5">
                    {t('recipes.nutrition.perServingResult', {
                      kcal: round(preview.perServing.kcal),
                      protein: round(preview.perServing.protein_g),
                      grams: round(preview.servingWeightG, 0) ?? '—',
                    })}
                  </p>
                  {preview.warnings
                    // VH-255: said once already, under the button.
                    .filter((warning) => warning.code !== 'ingredients_without_nutrition')
                    .map((warning) => (
                    <p
                      key={warning.code}
                      className="text-xs text-amber-800 mt-1 flex items-start gap-1"
                      data-testid={`preview-warning-${warning.code}`}
                    >
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      <span>
                        {t(`recipes.nutrition.warning.${warning.code}`, {
                          names: (warning.names ?? []).join(', '),
                          density: round(warning.kcal_per_100g_solids),
                          floor: warning.expected_at_least,
                          kcal: warning.kcal,
                          fromMacros: warning.kcal_from_macros,
                          raw: round(warning.raw_weight_g, 0),
                          defaultValue: warning.code,
                        })}
                      </span>
                    </p>
                  ))}
                </div>
              )}

              {summary && (
                <div className="rounded-md bg-gray-50 p-3 text-sm" data-testid="recipe-nutrition-summary">
                  <p className="font-medium text-gray-900">
                    {summary.per_100g
                      ? t('recipes.nutrition.per100gResult', {
                          kcal: round(summary.per_100g.kcal),
                          protein: round(summary.per_100g.protein_g),
                          carbs: round(summary.per_100g.carbs_g),
                          fat: round(summary.per_100g.fat_g),
                        })
                      : t('recipes.nutrition.noResult')}
                  </p>
                  <p className="text-gray-700 mt-0.5">
                    {t('recipes.nutrition.perServingResult', {
                      kcal: round(summary.per_serving.kcal),
                      protein: round(summary.per_serving.protein_g),
                      grams: round(summary.serving_weight_g, 0) ?? '—',
                    })}
                  </p>
                  {summary.yield_factor && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('recipes.nutrition.yield', { factor: summary.yield_factor })}
                    </p>
                  )}
                  {summary.warnings?.map((warning) => (
                    <p key={warning.code} className="text-xs text-amber-700 mt-0.5">
                      {t(`recipes.nutrition.warning.${warning.code}`, {
                        names: (warning.names ?? []).join(', '),
                        density: round(warning.kcal_per_100g_solids),
                        floor: warning.expected_at_least,
                        kcal: warning.kcal,
                        fromMacros: warning.kcal_from_macros,
                        raw: round(warning.raw_weight_g, 0),
                        defaultValue: warning.code,
                      })}
                    </p>
                  ))}
                </div>
              )}

              {confirmSave && (
                <div
                  className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
                  role="alertdialog"
                  aria-label={t('recipes.nutrition.guard.title')}
                  data-testid="nutrition-save-guard"
                >
                  <p className="font-medium text-amber-900">{t('recipes.nutrition.guard.title')}</p>
                  <ul className="list-disc ml-5 mt-1 text-amber-900">
                    {confirmSave.map((blocker) => (
                      <li key={blocker.code}>{blocker.text}</li>
                    ))}
                  </ul>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-outline text-sm"
                      onClick={() => setConfirmSave(null)}
                      data-testid="guard-back"
                    >
                      {t('recipes.nutrition.guard.back')}
                    </button>
                    <button
                      type="button"
                      className="btn-primary text-sm"
                      disabled={save.isPending}
                      onClick={() => save.mutate(buildPayload(true))}
                      data-testid="guard-save-anyway"
                    >
                      {t('recipes.nutrition.guard.saveAnyway')}
                    </button>
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <div className="flex justify-end">
                <button type="button" className="btn-primary text-sm" disabled={busy} onClick={submit}>
                  {save.isPending ? t('common.saving') : t('recipes.nutrition.save')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default RecipeNutritionPanel
